/**
 * Movimientos de Control de Kits — port de kit-stock/src/pages/Movements.tsx.
 */

import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { trpc } from '@/lib/trpc';
import { useKits } from './KitsModule';
import type { KitMovementType } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';
import { usePagination } from '@/hooks/usePagination';

export const MOVEMENT_LABELS: Record<string, string> = {
  ingreso: 'Ingreso de stock',
  entrega_individual: 'Entrega individual',
  entrega_multiple: 'Entrega múltiple',
  transferencia_enviada: 'Transferencia enviada',
  transferencia_recibida: 'Transferencia recibida',
  pedido_recibido: 'Pedido recibido',
  devolucion: 'Devolución',
  kit_danado: 'Kit dañado',
  perdida: 'Pérdida',
  ajuste_manual: 'Ajuste manual',
};

export function KitsMovements() {
  const { t } = useTranslation();
  const { activeOfficeId, offices, isGlobal } = useKits();

  const [filters, setFilters] = useState<{
    officeId: string | undefined;
    type: KitMovementType | undefined;
  }>({
    officeId: isGlobal ? undefined : activeOfficeId ?? undefined,
    type: undefined,
  });

  const { data: movements, isLoading } = trpc.kits.listMovements.useQuery({
    officeId: filters.officeId,
    type: filters.type,
  });
  const { pageItems: pageMovements, footer: paginationFooter } = usePagination(movements ?? [], 25);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('kits.common.movimientos')}</h1>
        <p className="text-muted-foreground mt-1">{t('kits.movements.historial_completo_de_movimientos_de_stock')}</p>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {isGlobal && (
            <div className="flex-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">{t('kits.common.clapbase')}</label>
              <Select
                value={filters.officeId || 'all'}
                onValueChange={(v) => setFilters({ ...filters, officeId: v === 'all' ? undefined : v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('kits.common.todas')}</SelectItem>
                  {offices.map((o) => (
                    <SelectItem key={o.officeId} value={o.officeId}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">{t('kits.movements.tipo_de_movimiento')}</label>
            <Select
              value={filters.type || 'all'}
              onValueChange={(v) => setFilters({ ...filters, type: v === 'all' ? undefined : (v as KitMovementType) })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('kits.common.todos')}</SelectItem>
                {Object.entries(MOVEMENT_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={() => setFilters({ officeId: isGlobal ? undefined : activeOfficeId ?? undefined, type: undefined })}
            >
              {t('kits.movements.limpiar_filtros')}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : !movements || movements.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <p className="text-lg">{t('kits.movements.no_hay_movimientos_registrados_con_los')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('kits.common.fecha')}</TableHead>
                  <TableHead>{t('kits.common.tipo')}</TableHead>
                  <TableHead>{t('kits.common.clapbase')}</TableHead>
                  <TableHead>{t('kits.common.usuario')}</TableHead>
                  <TableHead>{t('kits.common.persona')}</TableHead>
                  <TableHead className="text-right">{t('kits.common.cantidad')}</TableHead>
                  <TableHead className="text-right">{t('kits.movements.stock_resultante')}</TableHead>
                  <TableHead>{t('kits.common.nota')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageMovements.map((m) => (
                  <TableRow key={m.movementId}>
                    <TableCell className="font-mono text-sm">{format(new Date(m.createdAt), 'dd MMM yyyy HH:mm', { locale: es })}</TableCell>
                    <TableCell className="font-medium">{MOVEMENT_LABELS[m.type]}</TableCell>
                    <TableCell>{m.officeName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.userName}</TableCell>
                    <TableCell className="text-sm">{m.person || '—'}</TableCell>
                    <TableCell className="text-right font-semibold">{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</TableCell>
                    <TableCell className="text-right font-bold">{m.resultingStock}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{m.note || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="px-4 pb-3">{paginationFooter}</div>
          </div>
        )}
      </Card>
    </div>
  );
}
